import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  Chat,
  KeyValueGroup,
  KeyValueItem,
  Partner,
  Location,
  Project,
  Service,
  ToastConfig,
} from '@app/shared';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { ProjectUtilsService } from '../../services/project-utils.service';
import { environment } from '../../../../../environments/environment';
import { DrawerEscapeBlockerService, ScreenService, DocumentService, StServiceService } from '@app/core';
import { GlobalValuesService } from '../../../../core/services/global-values.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../../core/services/auth.service';

@Component({
  selector: 'app-service-drawer',
  standalone: false,

  templateUrl: './service-drawer.component.html',
  styleUrl: './service-drawer.component.less',
})
export class ServiceDrawerComponent implements OnInit, OnDestroy {
  mainRoute = environment.baseUrl;

  nzData: { project: Project | undefined; service: Service } =
    inject(NZ_DRAWER_DATA);

  routesList: Location[] = [];

  isMobile$;

  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#52C41A',
    content: '',
    showUndo: false,
  };

  @ViewChild('notificationTmpl') notificationTmpl: any;

  notRef: any;

  constructor(
    private drawerRef: NzDrawerRef<string>,
    public ps: ProjectUtilsService,
    private screenService: ScreenService,
    public globalValuesService: GlobalValuesService,
    private stServiceService: StServiceService,
    public authService: AuthService,
    private notificationService: NzNotificationService,
    private drawerEscapeBlocker: DrawerEscapeBlockerService,
  ) {
    this.globalValuesService.places$
      .pipe(takeUntilDestroyed())
      .subscribe((value) => (this.routesList = value));
    this.isMobile$ = screenService.isMobile$;
  }

  ngOnInit(): void {
    // Registrar handler nativo en fase de captura para interceptar Escape
    this.drawerEscapeBlocker.block();
  }

  close(): void {
    this.drawerRef.close(this.nzData);
  }

  saveService(): void {
    // Obtener los valores actualizados del servicio
    const updatedService = { ...this.nzData.service };

    // Enviar la actualización al servidor
    this.stServiceService.updateService(updatedService).subscribe({
      next: (response: any) => {
        console.log('Servicio actualizado:', response);

        // Configurar notificación de éxito
        this.toastConfig = {
          icon: 'check_circle',
          iconColor: '#52C41A',
          content: 'Se actualizó la macro tarea correctamente.',
          showUndo: false,
        };

        // Mostrar notificación usando el mismo formato que create
        this.notRef = this.notificationService.create(
          'blank',
          '',
          this.notificationTmpl,
          { nzPlacement: 'bottomRight' },
        );

        // Notificar que se actualizó un servicio (puede afectar las cards)
        this.ps.notifyOrdersCreated();

        // Cerrar el drawer después de guardar
        this.close();
      },
      error: (error: any) => {
        console.error('Error al actualizar servicio:', error);

        // Configurar notificación de error
        this.toastConfig = {
          icon: 'dangerous',
          iconColor: '#CB2A2F',
          content: 'Error al actualizar la macro tarea.',
          showUndo: false,
        };

        // Mostrar notificación usando el mismo formato que create
        this.notRef = this.notificationService.create(
          'blank',
          '',
          this.notificationTmpl,
          { nzPlacement: 'bottomRight' },
        );
      },
    });
  }

  setKeyValueData(): KeyValueGroup[] {
    const safePricing = this.nzData.service?.servicePricing || {};
    return [
      {
        layout: 'collapse',
        name: 'Información General',
        items: [
          {
            label: 'Nombre servicio',
            parentObject: this.nzData.service,
            component: 'ServiceDrawerComponent',
            editable: true,
            noWrap: true,
            required: true,
            type: 'text',
            path: 'name',
            value: this.nzData.service.name,
          },
          {
            label: 'Descripción',
            parentObject: this.nzData.service,
            component: 'ServiceDrawerComponent',
            editable: true,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'description',
            value: this.nzData.service.description,
          },
        ],
      },
      {
        layout: 'collapse',
        name: 'Agentes',
        items: [
          {
            parentObject: this.nzData.service,
            component: 'ServiceDrawerComponent',
            list: this.globalValuesService.providerList,
            label: 'Proveedor',
            editable: true,
            noWrap: false,
            required: false,
            type: 'avatar',
            path: 'supplierId',
            value: {
              userId: this.nzData.service.supplierId,
              centered: false,
            },
          },
          {
            label: 'Destinatario',
            parentObject: this.nzData.service,
            component: 'ServiceDrawerComponent',
            list: this.globalValuesService.consigneeList,
            editable: true,
            noWrap: false,
            required: false,
            type: 'avatar',
            path: 'consigneeId',
            value: {
              userId: this.nzData.service.consigneeId,
              centered: false,
            },
          },
          {
            label: 'Expedidor',
            parentObject: this.nzData.service,
            component: 'ServiceDrawerComponent',
            list: this.globalValuesService.shipperList,
            editable: true,
            noWrap: false,
            required: false,
            type: 'avatar',
            path: 'shipperId',
            value: {
              userId: this.nzData.service.shipperId,
              centered: false,
            },
          },
        ],
      },
      {
        layout: 'collapse',
        name: 'Ruta',
        items: [
          {
            label: 'Origen',
            parentObject: this.nzData.service,
            component: 'ServiceDrawerComponent',
            list: this.routesList,
            editable: true,
            noWrap: false,
            required: false,
            type: 'route',
            path: 'originId',
            value: {
              origin: this.nzData.service.originId,
            },
          },
          {
            label: 'Destino',
            parentObject: this.nzData.service,
            component: 'ServiceDrawerComponent',
            list: this.routesList,
            editable: true,
            noWrap: false,
            required: false,
            type: 'route',
            path: 'destinationId',
            value: {
              origin: this.nzData.service.destinationId,
            },
          },
        ],
      },
      {
        layout: 'collapse',
        name: 'Fechas',
        items: [
          {
            label: 'Fecha inicio planificada',
            parentObject: this.nzData.service,
            component: 'ServiceDrawerComponent',
            noWrap: false,
            editable: true,
            required: false,
            type: 'date',
            path: 'startDatePlan',
            value: {
              startDate: this.nzData.service.startDatePlan,
            },
          },
          {
            label: 'Fecha fin planificada',
            parentObject: this.nzData.service,
            component: 'ServiceDrawerComponent',
            noWrap: false,
            editable: true,
            required: false,
            type: 'date',
            path: 'endDatePlan',
            value: {
              startDate: this.nzData.service.endDatePlan,
            },
          },
        ],
      },
      {
        layout: 'collapse',
        name: 'Especificaciones',
        items: [
          {
            label: 'Especificaciones técnicas de la mercancía',
            parentObject: this.nzData.service,
            component: 'ServiceDrawerComponent',
            editable: true,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'technicalSpecifications',
            value: this.nzData.service.technicalSpecifications,
          },
          {
            label: 'Operaciones personalizadas',
            parentObject: this.nzData.service,
            component: 'ServiceDrawerComponent',
            editable: true,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'customOperations',
            value: this.nzData.service.customOperations,
          },
          {
            label: 'Frecuencia de entrega',
            parentObject: this.nzData.service,
            component: 'ServiceDrawerComponent',
            editable: true,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'frequency',
            value: this.nzData.service.frequency,
          },
        ],
      },
      {
        layout: 'collapse',
        name: 'Presupuesto',
        items: [
          {
            label: 'Cantidad',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            editable: false,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'totalQuantity',
            value:
              this.nzData.service?.servicePricing?.totalQuantity?.toString() ||
              '',
          },
          {
            label: 'Precio',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            editable: false,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'totalPrice',
            value:
              this.nzData.service?.servicePricing?.totalPrice?.toString() || '',
          },
          {
            label: 'Costo',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            editable: false,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'totalCost',
            value:
              this.nzData.service?.servicePricing?.totalCost?.toString() || '',
          },
          {
            label: 'Descuento',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            editable: false,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'totalDiscount',
            value:
              this.nzData.service?.servicePricing?.totalDiscount?.toString() ||
              '',
          },
          {
            label: 'VAT de ventas',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            editable:
              !!this.authService.loggedUser$.value?.editService &&
              !!this.nzData.service?.servicePricing,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'totalSaleVat',
            value:
              this.nzData.service?.servicePricing?.totalSaleVat?.toString() ||
              '',
          },
          {
            label: 'VAT total de compras',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            editable:
              !!this.authService.loggedUser$.value?.editService &&
              !!this.nzData.service?.servicePricing,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'totalPurchaseVat',
            value:
              this.nzData.service?.servicePricing?.totalPurchaseVat?.toString() ||
              '',
          },
          {
            label: 'Total unitario',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            type: 'select',
            path: 'totalUnit',
            list: this.globalValuesService.unitOfMeasureslist,
            selectLabel: 'name',
            selectValue: 'name',
            value: this.nzData.service?.servicePricing?.totalUnit || '',
            editable:
              !!this.authService.loggedUser$.value?.editService &&
              !!this.nzData.service?.servicePricing,
            noWrap: true,
          },
        ],
      },
      {
        layout: 'collapse',
        name: 'Presupuesto Unitaria',
        items: [
          {
            label: 'Cantidad unitaria',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            editable: false,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'unitQuantity',
            value:
              this.nzData.service?.servicePricing?.unitQuantity?.toString() ||
              '',
          },
          {
            label: 'Precio unitario',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            editable: false,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'unitPrice',
            value:
              this.nzData.service?.servicePricing?.unitPrice?.toString() || '',
          },
          {
            label: 'Costo unitario',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            editable: false,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'unitCost',
            value:
              this.nzData.service?.servicePricing?.unitCost?.toString() || '',
          },
          {
            label: 'Descuento unitario',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            editable: false,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'unitDiscount',
            value:
              this.nzData.service?.servicePricing?.unitDiscount?.toString() ||
              '',
          },
          {
            label: 'VAT de compras unitario',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            editable:
              !!this.authService.loggedUser$.value?.editService &&
              !!this.nzData.service?.servicePricing,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'unitPurchaseVat',
            value:
              this.nzData.service?.servicePricing?.unitPurchaseVat?.toString() ||
              '',
          },
          {
            label: 'VAT de ventas unitario',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            editable:
              !!this.authService.loggedUser$.value?.editService &&
              !!this.nzData.service?.servicePricing,
            noWrap: true,
            required: false,
            type: 'text',
            path: 'unitSaleVat',
            value:
              this.nzData.service?.servicePricing?.unitSaleVat?.toString() ||
              '',
          },
          {
            label: 'Unidad',
            parentObject: safePricing,
            component: 'ServiceDrawerComponent',
            type: 'select',
            path: 'unit',
            list: this.globalValuesService.unitOfMeasureslist,
            selectLabel: 'name',
            selectValue: 'name',
            value: this.nzData.service?.servicePricing?.unit || '',
            editable:
              !!this.authService.loggedUser$.value?.editService &&
              !!this.nzData.service?.servicePricing,
            noWrap: true,
          },
        ],
      },
    ];
  }

  docDrawerChange(isOpen: boolean): void {
    this.drawerRef.nzOffsetX = isOpen ? 20 : 0;
  }

  ngOnDestroy(): void {
    // Remover listener nativo si fue registrado
    this.drawerEscapeBlocker.unblock();
  }
}
